import { type FieldApi } from "@rvf/react-router";
import { type ExclusiveCheckboxInputProps } from "./ExclusiveCheckboxInput";
import { type ExclusiveCheckboxesType } from "~/services/validation/checkedCheckbox";
import { type CheckboxValue } from "../checkbox/Checkbox";

/**
 * Checkbox change handler, handles both normal checkboxes and the special "none" checkbox
 * Curried for cleaner function call signature in the DOM
 */
export const onCheckboxChange =
  (
    parentField: FieldApi<ExclusiveCheckboxesType | undefined>,
    checkboxes: Array<Omit<ExclusiveCheckboxInputProps, "onChange">>,
    noneCheckboxValue: CheckboxValue,
    setNoneCheckboxValue: React.Dispatch<React.SetStateAction<CheckboxValue>>,
    setCheckboxes: React.Dispatch<
      React.SetStateAction<Array<Omit<ExclusiveCheckboxInputProps, "onChange">>>
    >,
    setAnnouncement: React.Dispatch<React.SetStateAction<string>>,
  ) =>
  (checkboxName: string, checked: CheckboxValue) => {
    const existingParentValues =
      parentField.value() ??
      Object.fromEntries(checkboxes.map((c) => [c.name, c.value]));

    const label = checkboxes.find((c) => c.name.endsWith(checkboxName))?.label;

    // oxlint-disable unicorn/consistent-function-scoping
    const announce = (message: string) => {
      // this constant forces SR to re-announce
      setAnnouncement("");
      setTimeout(() => setAnnouncement(message), 10);
    };

    if (checkboxName === "none") {
      const newFieldValues =
        checked === "on"
          ? Object.fromEntries(
              Object.entries(existingParentValues).map(([key]) => [key, "off"]),
            )
          : parentField.value();
      parentField.setValue({ ...newFieldValues, none: checked });
      setNoneCheckboxValue(checked);
      if (checked === "on") {
        setCheckboxes((prev) => prev.map((c) => c && { ...c, value: "off" }));

        announce(
          `"Nicht trifft zu" aktiviert. Alle anderen Optionen wurden deaktiviert.`,
        );
      } else {
        announce(`"Nicht trifft zu" deaktiviert.`);
      }
      parentField.validate();
    } else {
      parentField.setValue({
        ...existingParentValues,
        [checkboxName]: checked,
      } as ExclusiveCheckboxesType);
      setCheckboxes((prev) =>
        prev.map((c) =>
          c?.name.split(".").pop() === checkboxName
            ? { ...c, value: checked }
            : c,
        ),
      );
      if (noneCheckboxValue === "on" && checked === "on") {
        setNoneCheckboxValue("off");
        announce(
          `${label} ausgewählt. "Nicht trifft zu" wurde automatisch deaktiviert.`,
        );
      } else {
        announce(
          checked === "on" ? `${label} ausgewählt.` : `${label} abgewählt.`,
        );
      }
      parentField.validate();
    }
  };
